import type { Bbox, GridSpec } from './types';

/** Longitude/latitude to Web Mercator, normalised to [0,1] with y growing south. */
export function lonToMercX(lon: number): number {
  return (lon + 180) / 360;
}

export function latToMercY(lat: number): number {
  const s = Math.min(85.051129, Math.max(-85.051129, lat));
  return 0.5 - Math.log(Math.tan(Math.PI / 4 + (s * Math.PI) / 360)) / (2 * Math.PI);
}

export function mercXToLon(x: number): number {
  return x * 360 - 180;
}

export function mercYToLat(y: number): number {
  return (Math.atan(Math.exp((0.5 - y) * 2 * Math.PI)) - Math.PI / 4) * (360 / Math.PI);
}

/**
 * The heatmap grid is uniform in Web Mercator so it maps onto the map with a
 * single textured quad and no resampling error. Cell ground size therefore
 * varies by ~1.2% across Berlin's latitude span, which is far below the
 * accuracy of the walking model, so distances use one average value.
 */
export function makeGrid(bbox: Bbox, targetCellM: number): GridSpec {
  const midLat = (bbox.minLat + bbox.maxLat) / 2;
  const widthM = (bbox.maxLon - bbox.minLon) * 111320 * Math.cos((midLat * Math.PI) / 180);
  const heightM = (bbox.maxLat - bbox.minLat) * 111132;
  const width = Math.max(16, Math.round(widthM / targetCellM));
  const height = Math.max(16, Math.round(heightM / targetCellM));
  return {
    width,
    height,
    minX: lonToMercX(bbox.minLon),
    maxX: lonToMercX(bbox.maxLon),
    // Mercator y is inverted: the north edge has the smaller y.
    minY: latToMercY(bbox.maxLat),
    maxY: latToMercY(bbox.minLat),
    bbox,
    cellM: (widthM / width + heightM / height) / 2,
  };
}

/** Metres per cell along each axis (averaged over the grid). */
export function cellMeters(grid: GridSpec): { mx: number; my: number } {
  const midLat = (grid.bbox.minLat + grid.bbox.maxLat) / 2;
  const widthM = (grid.bbox.maxLon - grid.bbox.minLon) * 111320 * Math.cos((midLat * Math.PI) / 180);
  const heightM = (grid.bbox.maxLat - grid.bbox.minLat) * 111132;
  return { mx: widthM / grid.width, my: heightM / grid.height };
}

/** Fractional grid column/row of a coordinate (may fall outside the grid). */
export function lonLatToCell(grid: GridSpec, lon: number, lat: number): { col: number; row: number } {
  const x = lonToMercX(lon);
  const y = latToMercY(lat);
  return {
    col: ((x - grid.minX) / (grid.maxX - grid.minX)) * grid.width,
    row: ((y - grid.minY) / (grid.maxY - grid.minY)) * grid.height,
  };
}

/** Centre coordinate of a cell. */
export function cellToLonLat(grid: GridSpec, col: number, row: number): { lon: number; lat: number } {
  const x = grid.minX + ((col + 0.5) / grid.width) * (grid.maxX - grid.minX);
  const y = grid.minY + ((row + 0.5) / grid.height) * (grid.maxY - grid.minY);
  return { lon: mercXToLon(x), lat: mercYToLat(y) };
}

export const QUALITY_PRESETS = [
  { id: 'fast', label: 'Fast', cellM: 150 },
  { id: 'balanced', label: 'Balanced', cellM: 95 },
  { id: 'fine', label: 'Fine', cellM: 65 },
] as const;

export type QualityId = (typeof QUALITY_PRESETS)[number]['id'];
